/**
 * Utilidad de logging estructurado para la aplicacion.
 * Permite registrar mensajes en consola con diferentes niveles y contexto.
 * Todos los niveles estan deshabilitados en produccion y tests.
 *
 * Niveles soportados: debug, info, warn, error, apiError (solo via `logger`).
 *
 * @example
 * ```typescript
 * import { logger } from '@/shared/common/utils/logger.util'
 *
 * logger.info('Mensaje informativo', { contexto: 'opcional' })
 * logger.error('Mensaje de error', { contexto: 'opcional' }, new Error('Detalle'))
 * logger.apiError(404, '/api/users/99', new Error('Not found'), { params: { id: 99 } })
 * ```
 */

/**
 * Niveles de logging disponibles
 * @enum {string}
 */
enum LogLevel {
  /** Nivel de debug - deshabilitado en produccion y tests */
  DEBUG = 'debug',
  /** Nivel informativo - deshabilitado en produccion y tests */
  INFO = 'info',
  /** Nivel de advertencia - deshabilitado en produccion y tests */
  WARN = 'warn',
  /** Nivel de error - deshabilitado en produccion y tests */
  ERROR = 'error',
}

// Mostrar logs en todos los entornos menos produccion y tests
const isLogEnabled = import.meta.env.VITE_NODE_ENV !== 'production' && import.meta.env.NODE_ENV !== 'test'

/**
 * Formatea el mensaje de log con timestamp, nivel y contexto.
 * @param level - Nivel del log.
 * @param message - Mensaje descriptivo.
 * @param context - Contexto adicional opcional.
 * @returns Mensaje formateado con timestamp y nivel.
 */
function formatMessage(level: LogLevel, message: string, context?: Record<string, unknown>): string {
  const timestamp = new Date().toISOString()
  const contextStr = context ? ` | ${JSON.stringify(context)}` : ''
  return `[${timestamp}] ${level.toUpperCase()}: ${message}${contextStr}`
}

/**
 * Imprime el mensaje en consola segun el nivel.
 * @param level - Nivel del log.
 * @param formattedMessage - Mensaje ya formateado.
 * @param err - Error asociado (solo para logs de error).
 */
function logToConsole(level: LogLevel, formattedMessage: string, err?: Error): void {
  switch (level) {
    case LogLevel.DEBUG:
      console.debug(formattedMessage)
      break
    case LogLevel.INFO:
      console.info(formattedMessage)
      break
    case LogLevel.WARN:
      console.warn(formattedMessage)
      break
    case LogLevel.ERROR:
      console.error(formattedMessage, err)
      break
  }
}

/**
 * Funcion principal de logging.
 * @param level - Nivel del log.
 * @param message - Mensaje descriptivo.
 * @param context - Contexto adicional opcional.
 * @param err - Error asociado (solo para logs de error).
 */
function log(level: LogLevel, message: string, context?: Record<string, unknown>, err?: Error): void {
  if (!isLogEnabled) return
  const formattedMessage = formatMessage(level, message, context)
  logToConsole(level, formattedMessage, err)
}

/**
 * Registra un mensaje de nivel debug. Deshabilitado en produccion y tests.
 * @param message - Mensaje descriptivo.
 * @param context - Contexto adicional opcional.
 * @example
 * ```typescript
 * debug('Procesando datos', { totalItems: 25 })
 * // [2024-01-15T16:35:10.123Z] DEBUG: Procesando datos | {"totalItems":25}
 * ```
 */
function debug(message: string, context?: Record<string, unknown>): void {
  log(LogLevel.DEBUG, message, context)
}

/**
 * Registra un mensaje de nivel info. Deshabilitado en produccion y tests.
 * @param message - Mensaje descriptivo.
 * @param context - Contexto adicional opcional.
 * @example
 * ```typescript
 * info('Usuario logueado', { username: 'demo' })
 * // [2024-01-15T16:35:10.123Z] INFO: Usuario logueado | {"username":"demo"}
 * ```
 */
function info(message: string, context?: Record<string, unknown>): void {
  log(LogLevel.INFO, message, context)
}

/**
 * Registra un mensaje de nivel warn. Deshabilitado en produccion y tests.
 * @param message - Mensaje descriptivo.
 * @param context - Contexto adicional opcional.
 * @example
 * ```typescript
 * warn('Datos incompletos', { received: ['id'], expected: ['id', 'name'] })
 * // [2024-01-15T16:35:10.123Z] WARN: Datos incompletos | {"received":["id"],...}
 * ```
 */
function warn(message: string, context?: Record<string, unknown>): void {
  log(LogLevel.WARN, message, context)
}

/**
 * Registra un mensaje de nivel error. Deshabilitado en produccion y tests.
 * @param message - Mensaje descriptivo.
 * @param context - Contexto adicional opcional.
 * @param err - Error asociado.
 * @example
 * ```typescript
 * error('Error al cargar datos', { endpoint: '/api/users' }, new Error('Timeout'))
 * // [2024-01-15T16:35:10.123Z] ERROR: Error al cargar datos | {"endpoint":"/api/users"} Error: Timeout
 * ```
 */
function error(message: string, context?: Record<string, unknown>, err?: Error): void {
  log(LogLevel.ERROR, message, context, err)
}

/**
 * Registra un error de API con status y URL. Wrapper especializado de error().
 * @param status - Codigo de estado HTTP.
 * @param url - URL de la solicitud.
 * @param err - Error asociado.
 * @param context - Contexto adicional opcional.
 * @example
 * ```typescript
 * apiError(404, '/api/users/99', new Error('Not found'), { params: { id: 99 } })
 * // [2024-01-15T16:35:10.123Z] ERROR: API Error: 404 /api/users/99 | {"params":{"id":99}}
 * ```
 */
function apiError(status: number, url: string, err?: Error, context?: Record<string, unknown>): void {
  error(`API Error: ${status} ${url}`, context, err)
}

/**
 * Punto unico de acceso al logging (debug, info, warn, error, apiError).
*/
export const logger = {
  debug,
  info,
  warn,
  error,
  apiError,
}